<p-confirmDialog key="ci-dialog" styleClass="ci-confirm-dialog">
  <ng-template #headless let-message let-onAccept="onAccept" let-onReject="onReject">
    <div class="flex flex-col items-center p-8 bg-white rounded-xl w-full max-w-lg mx-auto shadow-xl">
      <div class="rounded-full bg-amber-500 text-white inline-flex justify-center items-center h-24 w-24 -mt-20 shadow-lg">
        <i class="pi pi-exclamation-triangle text-5xl"></i>
      </div>

      <span class="font-bold text-2xl text-center mt-6 mb-2 text-gray-800">
        {{ message.header }}
      </span>

      <p class="text-center text-xl leading-relaxed text-gray-700" [innerHTML]="message.message"></p>

      <div class="flex justify-center gap-6 mt-8 w-full">
        <p-button [label]="message.rejectLabel" (click)="onReject()" variant="outlined" styleClass="cs-button-touch" />
        <p-button [label]="message.acceptLabel" (click)="onAccept()" styleClass="cs-button-touch" />
      </div>
    </div>
  </ng-template>
</p-confirmDialog>

<header class="flex header p-2">
  <h2>{{ processName }}</h2>
</header>

<section class="content">
  <div class="consumption-notice mb-3">
    <p-message severity="info" styleClass="w-full">{{ getConsumptionNoticeMessage() }}</p-message>
  </div>

  @if (hasInsufficientInventory()) {
    <div class="insufficient-inventory-warning mb-3">
      <p-message severity="warn" styleClass="w-full">
        <div>
          <strong>{{ 'Recipe_Values_Insufficient_Inventory_Title' | translateWF: 'Insufficient inventory for the following items:' }}</strong>
          <ul>
            @for (item of getInsufficientInventoryItems(); track item.itemCode) {
              <li>
                {{ item.itemCode }} — {{ item.itemDescription }}: {{ 'Recipe_Values_Insufficient_Inventory_Needs' | translateWF: 'needs' }} {{ item.required | number: '1.0-5' }},
                {{ 'Recipe_Values_Insufficient_Inventory_Available' | translateWF: 'available' }} {{ item.available | number: '1.0-5' }}
              </li>
            }
          </ul>
        </div>
      </p-message>
    </div>
  }

  <div class="flex justify-between items-center mb-3">
    <h3 class="section-title m-0">Consumed Items</h3>
    @if (canEdit && !isCompleted) {
      <p-button label="Add Item" icon="pi pi-plus" size="small" (onClick)="addItem()" [disabled]="isLoadingItems || isSaving" />
    }
  </div>

  <loading-overlay [isLoading]="isLoadingItems"></loading-overlay>

  <form [formGroup]="consumedItemsForm">
    <p-table [value]="items.controls" formArrayName="items">
      <ng-template pTemplate="header">
        <tr>
          <th style="width: 45%">Item *</th>
          <th style="width: 15%">Qty *</th>
          <th style="width: 12%">Unit *</th>
          <th style="width: 18%">{{ 'Recipe_Values_Current_Inventory' | translateWF: 'Current Inventory' }}</th>
          <th style="width: 10%"></th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-itemControl let-i="rowIndex">
        <tr [formGroupName]="i">
          <td>
            <p-autoComplete
              formControlName="itemCode"
              [forceSelection]="false"
              [dropdown]="true"
              #itemSearch="cslServerSearch"
              [cslServerSearch]="fetchItems"
              [suggestions]="itemSearch.results()"
              (onSelect)="onItemSelect($event, i)"
              appendTo="body"
              optionValue="itemCode"
              optionLabel="optionDisplayName"
              styleClass="w-full"
              [inputStyleClass]="isFieldInvalid(i, 'itemCode') ? 'ng-invalid ng-dirty' : ''"
            >
              <ng-template let-item pTemplate="item">
                <div class="flex align-items-center gap-2">
                  <div class="text-sm">
                    <b>{{ item.itemCode }}</b> {{ item.itemDescription | localized }}
                  </div>
                </div>
              </ng-template>
            </p-autoComplete>
          </td>
          <td>
            <p-inputNumber
              formControlName="quantity"
              mode="decimal"
              [minFractionDigits]="2"
              [maxFractionDigits]="3"
              [min]="0.01"
              placeholder="0.00"
              styleClass="w-full"
              [inputStyleClass]="isFieldInvalid(i, 'quantity') ? 'ng-invalid ng-dirty' : ''"
            />
            <small *ngIf="isFieldInvalid(i, 'quantity')" class="p-error"> Quantity must be greater than 0 </small>
          </td>
          <td>
            <p-select
              formControlName="unitOfMeasure"
              [options]="unitOfMeasureOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select unit"
              styleClass="w-full"
              appendTo="body"
              [class.ng-invalid]="isFieldInvalid(i, 'unitOfMeasure')"
              [class.ng-dirty]="isFieldInvalid(i, 'unitOfMeasure')"
            />
            <small *ngIf="isFieldInvalid(i, 'unitOfMeasure')" class="p-error"> Unit of measure is required </small>
          </td>
          <td>
            {{ getCurrentInventory(itemControl.get('itemCode')?.value) ?? '--' | number: '1.0-5' }}
          </td>
          <td class="text-center">
            @if (canEdit && !isCompleted) {
              <p-button
                *ngIf="items.length > 1"
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                size="small"
                (onClick)="removeItem(i)"
                [disabled]="isLoadingItems || isSaving"
              />
            }
          </td>
        </tr>
      </ng-template>
    </p-table>
  </form>
</section>

<footer>
  <p-button label="Cancel" variant="outlined" (onClick)="onClose()" [disabled]="isSaving" />
  @if (canEdit && !isCompleted) {
    <p-button label="Save Draft" severity="secondary" [outlined]="true" [loading]="isSaving" [disabled]="consumedItemsForm.invalid || isLoadingItems" (onClick)="saveDraft()" />
    <p-button
      label="Mark as Complete"
      severity="success"
      [loading]="isSaving"
      [disabled]="consumedItemsForm.invalid || isLoadingItems || hasInsufficientInventory()"
      (onClick)="markAsComplete()"
    />
  }
</footer>
